<template>
    <Dialog v-model:open="opened">
        <DialogTrigger as-child>
            <slot />
        </DialogTrigger>
        <DialogContent class="max-w-[660px]">
            <DialogHeader>
                <template #icon><User class="text-z-primary-600" /></template>
                <DialogTitle>{{
                    $t("billing.billing_information.modal_title")
                }}</DialogTitle>
                <DialogDescription>
                    {{ $t("billing.billing_information.modal_description") }}
                </DialogDescription>
            </DialogHeader>

            <ScrollArea class="max-h-[calc(100vh-300px)]">
                <form
                    id="billing-details-form"
                    class="mt-4 max-w-3xl space-y-3 rounded-lg"
                    @submit.prevent="update"
                >
                    <!-- <div class="grid w-full items-center gap-1.5 px-4">
            <Label for="email-address">{{
              $t("billing.billing_information.email")
            }}</Label>
            <Input
              id="email-address"
              v-model="form.email"
              type="email"
              autocomplete="email"
            />
          </div> -->

                    <div class="px-4">
                        <RadioGroup
                            class="gap-0"
                            v-model="emailToggleState"
                            default-value="default"
                            :orientation="'vertical'"
                        >
                            <div class="space-x-2 flex items-center">
                                <RadioGroupItem id="r1" value="default" />
                                <Label for="r1"
                                    >{{
                                        $t(
                                            "billing.billing_information.send_to_registered_address",
                                        )
                                    }}
                                </Label>
                            </div>

                            <div
                                class="pl-6 text-sm text-iron-500 tracking-[-0.01em]"
                            >
                                {{ page.props.user?.email || "" }}
                            </div>
                            <div class="mt-6 space-x-2 flex items-center">
                                <RadioGroupItem id="r2" value="custom" />
                                <Label for="r2">
                                    {{
                                        $t(
                                            "billing.billing_information.send_invoice_to_different_email",
                                        )
                                    }}</Label
                                >
                            </div>

                            <Transition>
                                <div
                                    v-if="emailToggleState === 'custom'"
                                    class="mt-1 pl-6 text-sm tracking-[-0.01em]"
                                >
                                    <div class="relative items-center">
                                        <Input
                                            id="email-address"
                                            v-model="form.email"
                                            type="email"
                                            autocomplete="email"
                                            class="pl-10"
                                        />
                                        <span
                                            class="inset-y-0 start-0 px-3 absolute flex items-center justify-center"
                                        >
                                            <Mail
                                                class="size-5 text-iron-500"
                                            />
                                        </span>
                                    </div>
                                </div>
                            </Transition>
                        </RadioGroup>
                    </div>
                    <hr />
                    <div class="space-y-3 p-4">
                        <div class="gap-4 sm:gap-6 grid grid-cols-4">
                            <div class="sm:col-span-2 col-span-4">
                                <div class="gap-1.5 grid w-full items-center">
                                    <Label for="first-name">{{
                                        $t(
                                            "billing.billing_information.first_name",
                                        )
                                    }}</Label>
                                    <Input
                                        id="first-name"
                                        v-model="form.first_name"
                                        type="text"
                                    />
                                </div>
                            </div>

                            <div class="sm:col-span-2 col-span-4">
                                <div class="gap-1.5 grid w-full items-center">
                                    <Label for="last-name">{{
                                        $t(
                                            "billing.billing_information.last_name",
                                        )
                                    }}</Label>
                                    <Input
                                        id="last-name"
                                        v-model="form.last_name"
                                        type="text"
                                        autocomplete="last-name"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="sm:col-span-2 col-span-4">
                            <div class="gap-1.5 grid w-full items-center">
                                <Label for="company">{{
                                    $t("billing.billing_information.company")
                                }}</Label>
                                <Input
                                    id="company"
                                    v-model="form.company"
                                    type="text"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-2 col-span-4">
                            <div class="gap-1.5 grid w-full items-center">
                                <Label for="street">{{
                                    $t("billing.billing_information.street")
                                }}</Label>
                                <Input
                                    id="street"
                                    v-model="form.street"
                                    type="text"
                                    autocomplete="street"
                                />
                            </div>
                        </div>

                        <div class="gap-4 sm:gap-6 grid grid-cols-4">
                            <div class="sm:col-span-2 col-span-4">
                                <div class="gap-1.5 grid w-full items-center">
                                    <Label for="city">{{
                                        $t("billing.billing_information.city")
                                    }}</Label>
                                    <Input
                                        id="city"
                                        v-model="form.city"
                                        type="text"
                                        autocomplete="city"
                                    />
                                </div>
                            </div>

                            <div class="sm:col-span-2 col-span-4">
                                <div class="gap-1.5 grid w-full items-center">
                                    <Label for="postal-code">{{
                                        $t("billing.billing_information.zip")
                                    }}</Label>
                                    <Input
                                        id="postal-code"
                                        v-model="form.postal_code"
                                        type="text"
                                        autocomplete="postal-code"
                                    />
                                </div>
                            </div>
                        </div>

                        <div>
                            <CountrySelect
                                v-model="form.country"
                                :countries="countries"
                            >
                            </CountrySelect>
                        </div>
                    </div>
                    <hr />

                    <div class="p-4">
                        <div class="gap-4 sm:gap-6 grid grid-cols-4">
                            <div class="sm:col-span-2 col-span-4">
                                <div class="gap-1.5 grid w-full items-center">
                                    <Label for="crn">{{
                                        $t("billing.billing_information.crn")
                                    }}</Label>
                                    <Input
                                        id="crn"
                                        v-model="form.crn"
                                        type="text"
                                        autocomplete="crn"
                                    />
                                </div>
                            </div>
                            <div class="sm:col-span-2 col-span-4">
                                <div class="gap-1.5 grid w-full items-center">
                                    <Label for="tax_id">{{
                                        $t("billing.billing_information.tax_id")
                                    }}</Label>
                                    <Input
                                        id="tax_id"
                                        v-model="form.tax_id"
                                        type="text"
                                        autocomplete="tax_id"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </ScrollArea>
            <DialogFooter>
                <DialogClose>
                    <Button variant="outline" class="sm:w-auto w-full">
                        {{ $t("close") }}
                    </Button>
                </DialogClose>
                <Button
                    type="submit"
                    :loading="form.processing"
                    form="billing-details-form"
                >
                    {{ $t("save_changes") }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import route from "ziggy-js";
import { BillingAddress } from "../types";
import { countries } from "./countries";
import { useForm } from "@inertiajs/vue3";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import CountrySelect from "./CountrySelect.vue";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog-v2";
import { Mail, User } from "lucide-vue-next";
import { ScrollArea } from "@/components/ui/scroll-area";
import { usePage } from "@inertiajs/vue3";
import { ref, watch } from "vue";

const props = defineProps<{
    billingAddress: BillingAddress;
}>();

const page = usePage();

const opened = ref(false);

const emailToggleState = ref<"default" | "custom">(
    page.props.user?.email === props.billingAddress.email
        ? "default"
        : "custom",
);

// When reseting the email to the default value, we set the account email as the billing email
watch(emailToggleState, (newValue) => {
    if (newValue === "default") {
        form.email = page.props.user?.email || "";
    }
});

const form = useForm({
    email: props.billingAddress.email ?? "",
    company: props.billingAddress.company ?? "",
    first_name: props.billingAddress.first_name ?? "",
    last_name: props.billingAddress.last_name ?? "",
    city: props.billingAddress.city ?? "",
    street: props.billingAddress.street ?? "",
    country: props.billingAddress.country.code,
    postal_code: props.billingAddress.postal_code ?? "",
    tax_id: props.billingAddress.tax_id ?? "",
    crn: props.billingAddress.crn ?? "",
});

function update() {
    form.put(
        route("app.billing-addresses.update", {
            billingAddress: props.billingAddress.id,
        }),
        {
            onSuccess: () => {
                opened.value = false;
            },
        },
    );
}
</script>
